import React, { useState, useEffect } from "react";
import useUsers from "../../hooks/utilisateur/useUsers";
import Loader from "../components/loader";

import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";

import { FiSearch, FiFilter } from "react-icons/fi";

function UsersDesactive() {
  const { users, updateUser, fetchUsers } = useUsers();

  const [message, setMessage] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);

  const usersPerPage = 10;

  // ================= FETCH USERS =================

  useEffect(() => {
    const loadUsers = async () => {
      try {
        setLoading(true);
        await fetchUsers();
      } catch (error) {
        console.error("Erreur chargement utilisateurs désactivés :", error);
      } finally {
        setLoading(false);
      }
    };

    loadUsers();
  }, []);

  // ================= ACTIVATE USER =================

  const handleActiveUser = (userId) => {
    confirmAlert({
      title: "Confirmation",
      message: "Êtes-vous sûr de vouloir activer cet utilisateur ?",
      buttons: [
        {
          label: "Oui",
          onClick: () => {
            updateUser(userId, { active: 1 }, () => {
              setMessage({
                type: "success",
                text: "Utilisateur activé avec succès.",
              });

              setTimeout(() => setMessage(null), 2000);
            });
          },
        },
        { label: "Non" },
      ],
    });
  };

  // ================= FILTER USERS =================

  const filteredUsers = users
    .filter((user) => user.active === 0)
    .filter((user) => {
      const fullText = `${user.nom} ${user.prenom} ${user.email}`.toLowerCase();
      return fullText.includes(searchTerm.toLowerCase());
    })
    .filter((user) => {
      if (roleFilter === "all") return true;
      return user.role === Number(roleFilter);
    });

  // ================= PAGINATION =================

  const totalPages = Math.ceil(filteredUsers.length / usersPerPage);
  const indexOfLastUser = currentPage * usersPerPage;
  const indexOfFirstUser = indexOfLastUser - usersPerPage;
  const currentUsers = filteredUsers.slice(indexOfFirstUser, indexOfLastUser);

  const goToNextPage = () => {
    if (currentPage < totalPages) setCurrentPage(currentPage + 1);
  };

  const goToPrevPage = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1);
  };

  // ================= RENDER =================

  return (
    <div className="container-fluid">
      {/* TITLE */}
      <div className="d-flex align-items-center justify-content-between mb-4">
        <h2 className="h4 mb-0">Comptes désactivés</h2>
      </div>

      {/* ALERT MESSAGE */}
      {message && (
        <div
          className={`alert ${
            message.type === "success" ? "alert-success" : "alert-danger"
          }`}
          role="alert"
        >
          {message.text}
        </div>
      )}

      <div className="card shadow-sm mb-4">
        <div className="card-header bg-white">
          <h3 className="h6 mb-0">Liste des comptes désactivés</h3>
        </div>

        <div className="card-body">
          {loading ? (
            <Loader />
          ) : (
            <>
              <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                {/* SEARCH */}
                <div className="position-relative" style={{ width: "300px" }}>
                  <FiSearch className="position-absolute top-50 start-0 translate-middle-y ms-2 text-secondary" />
                  <input
                    type="text"
                    className="form-control ps-4 rounded-pill"
                    placeholder="Rechercher utilisateur..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    style={{ paddingLeft: "35px" }}
                  />
                </div>

                {/* FILTER */}
                <div className="d-flex align-items-center gap-2">
                  <FiFilter className="text-secondary" />
                  <select
                    className="form-select rounded-pill"
                    value={roleFilter}
                    onChange={(e) => setRoleFilter(e.target.value)}
                    style={{ width: "180px" }}
                  >
                    <option value="all">Tous les rôles</option>
                    <option value="1">Admin</option>
                    <option value="2">Superviseur</option>
                    <option value="3">Client</option>
                  </select>
                </div>
              </div>

              {/* TABLE */}
              <div className="table-responsive">
                <table className="table align-middle">
                  <thead>
                    <tr>
                      <th>Nom</th>
                      <th>Prénom</th>
                      <th>Email</th>
                      <th>Rôle</th>
                      <th>Actions</th>
                    </tr>
                  </thead>

                  <tbody>
                    {currentUsers.length === 0 ? (
                      <tr>
                        <td
                          colSpan={5}
                          className="text-center text-secondary py-4"
                        >
                          Aucun compte désactivé
                        </td>
                      </tr>
                    ) : (
                      currentUsers.map((user) => (
                        <tr key={user._id}>
                          <td>{user.nom}</td>
                          <td>{user.prenom}</td>
                          <td>{user.email}</td>
                          <td>
                            {user.role === 1
                              ? "Admin"
                              : user.role === 2
                                ? "Superviseur"
                                : user.role === 3
                                  ? "Client"
                                  : "Inconnu"}
                          </td>
                          <td>
                            <button
                              className="btn btn-success btn-sm"
                              onClick={() => handleActiveUser(user._id)}
                            >
                              Activer
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* PAGINATION */}
              <div className="d-flex justify-content-between align-items-center mt-3">
                <button
                  className="btn btn-outline-secondary btn-sm"
                  onClick={goToPrevPage}
                  disabled={currentPage === 1}
                >
                  Précédent
                </button>

                <span className="small text-secondary">
                  Page {currentPage} sur {totalPages || 1}
                </span>

                <button
                  className="btn btn-outline-secondary btn-sm"
                  onClick={goToNextPage}
                  disabled={currentPage === totalPages || totalPages === 0}
                >
                  Suivant
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default UsersDesactive;
